// ─────────────────────────────────────────────────────────────────────────────
// StatCard.js — one KPI tile in the top row. The value counts up from 0 on
// mount; floats count over hundredths so the animation lands on 84.2, not 84.
// ─────────────────────────────────────────────────────────────────────────────
import { useCountUp } from "../../../shared/hooks";
import { statValue } from "../format";
import { ReactComponent as UpIcon } from "../../../assets/icons/up.svg";
import { ReactComponent as DownIcon } from "../../../assets/icons/down.svg";
import styles from "./StatCard.module.css";

/**
 * @param {object}    props
 * @param {ReactNode} props.icon      glyph for the top-right chip
 * @param {string}    props.label     uppercase caption
 * @param {number}    props.value     the raw metric
 * @param {string}    [props.unit]    small unit printed after the value
 * @param {boolean}   [props.float]   count over hundredths and keep 1–2 decimals
 * @param {"up"|"down"} props.trend
 * @param {string}    props.trendVal  pre-formatted delta, e.g. "+12.4%"
 * @param {string}    props.color     accent for the icon + the corner glow
 * @param {number}    [props.delay]   stagger added to the count-up duration (ms)
 */
export default function StatCard({
  icon, label, value, unit, float, trend, trendVal, color, delay = 0,
}) {
  const isFloat = !!float;
  const raw = useCountUp(isFloat ? Math.floor(value * 100) : value, 1200 + delay);
  const up = trend === "up";

  return (
    <div className={styles.card}>
      <div className={styles.top}>
        <span className={styles.label}>{label}</span>
        <span className={styles.icon} style={{ color }}>{icon}</span>
      </div>

      <div className={styles.value}>
        {statValue(raw, isFloat)}
        {unit && <span className={styles.unit}>{unit}</span>}
      </div>

      <div className={`${styles.trend} ${up ? styles.trendUp : styles.trendDown}`}>
        {up ? <UpIcon /> : <DownIcon />}
        <span>{trendVal}</span>
        <span className={styles.trendLabel}>vs last week</span>
      </div>

      <div className={styles.glow} style={{ background: color }} />
    </div>
  );
}

/**
 * The tile's loading stand-in. Same wrapper element as the real card so the grid
 * doesn't reflow when data lands — and deliberately NOT dimmed via opacity: the
 * card is the ancestor of its own ::before, which carries the backdrop-filter,
 * and opacity < 1 there would make it a backdrop root and kill the glass (see
 * the .card note in StatCard.module.css). The placeholder text is dimmed instead.
 */
export function StatCardSkeleton() {
  return (
    <div className={styles.card}>
      <div className={`${styles.value} ${styles.valueMuted}`}>—</div>
    </div>
  );
}
